import { Component, HostListener, inject, signal, computed } from '@angular/core';
import { RouterLink, RouterLinkActive, Router } from '@angular/router';
import { AuthService } from '@services/auth.service';
import { CommonModule } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { TelemetryService } from '@services/telemetry.service';
import {
  ChevronLeft,
  ChevronRight,
  LogOut,
  LucideAngularModule,
  User,
  UserCog,
  Users,
  X,
  Mail,
  Bell,
  Building2,
  Settings,
  UploadCloud,
  Zap,
} from 'lucide-angular';
import { environment } from '@environments/environment';
import { AvatarComponent } from '@components/ui/avatar.component';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    RouterLink,
    RouterLinkActive,
    CommonModule,
    TranslocoDirective,
    LucideAngularModule,
    AvatarComponent,
  ],
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
})
export class SidebarComponent {
  readonly ChevronLeft = ChevronLeft;
  readonly ChevronRight = ChevronRight;
  readonly Users = Users;
  readonly UserCog = UserCog;
  readonly User = User;
  readonly LogOut = LogOut;
  readonly X = X;
  readonly Mail = Mail;
  readonly Bell = Bell;
  readonly Building2 = Building2;
  readonly Settings = Settings;
  readonly UploadCloud = UploadCloud;
  readonly Zap = Zap;

  showNewFeatures = (environment as any)?.showNewFeatures ?? false;
  schedulerEnabled = (environment as any)?.features?.scheduler ?? false;

  auth = inject(AuthService);
  private router = inject(Router);
  private telemetry = inject(TelemetryService);
  
  // Initialize with explicit value to prevent ExpressionChangedAfterItHasBeenCheckedError
  isCollapsed = signal<boolean>(false);
  isMobileMenuOpen = signal<boolean>(false);
  isProfileMenuOpen = signal<boolean>(false);

  userName = computed(() => {
    const user = this.auth.storedUser();
    if (user?.name?.trim()) {
      return user.name.trim();
    }
    if (user?.email) {
      const namePart = user.email.split('@')[0];
      return namePart.charAt(0).toUpperCase() + namePart.slice(1);
    }
    return 'User';
  });

  userInitial = computed(() => this.userName().charAt(0).toUpperCase());

  readonly linkedCustomerPortals = computed(
    () => this.auth.storedUser()?.customers ?? []
  );

  readonly showPortalSwitcher = computed(() => {
    const user = this.auth.storedUser();
    return user?.role === 'MspManager' && this.linkedCustomerPortals().length > 0;
  });

  toggleCollapse() {
    // Only allow collapse on desktop (>768px)
    if (window.innerWidth > 768) {
      this.isCollapsed.update((v) => !v);
    }
    this.telemetry.trackEvent('sidebar_toggled', { collapsed: this.isCollapsed() });
  }

  toggleMobileMenu() {
    this.isMobileMenuOpen.update((v) => !v);
    // Always show expanded sidebar on mobile
    if (window.innerWidth <= 768) {
      this.isCollapsed.set(false);
    }
  }

  closeMobileMenu() {
    this.isMobileMenuOpen.set(false);
  }

  toggleProfileMenu() {
    this.isProfileMenuOpen.update((v) => !v);
  }

  closeProfileMenu() {
    this.isProfileMenuOpen.set(false);
  }

  navigateToProfile() {
    this.router.navigate(['/profile']);
    this.closeProfileMenu();
    this.telemetry.trackEvent('navigate_to_profile');
  }

  openLinkedCustomerPortal(subDomain: string) {
    window.open(
      `https://${subDomain}.${environment.portalDomain}/#/login`,
      '_blank',
      'noopener,noreferrer'
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    // Close profile menu if clicking outside
    if (this.isProfileMenuOpen() && !target.closest('.sidebar-profile')) {
      this.closeProfileMenu();
    }
  }
}
